import React from "react";
import { Table, Tooltip, Typography, Tag, Space, Empty } from "antd";
import Helper from "../../../core/Helper"; // Projenizdeki yol
import { t } from "i18next";
import { Currency } from "../../Payment/MultiplePaymentApp"; // Orijinal import
import { useUser } from "../../../core/context/UserContext";

const { Text } = Typography;

export default function PaymentsPreview({ payments, appointmentCurrency }) {
    const { formatPrice } = useUser();

    // Silinmiş ödemeler listede gri olarak görünür ama toplamlara girmez
    const activePayments = (payments || []).filter(p => !p.isDeleted);

    // --- HESAPLAMALAR ---
    const totalEquivalent = activePayments.length > 0
        ? activePayments.map(x => x.currencyAmount).reduce((a, b) => a + b, 0)
        : 0;

    // Farklı para birimlerine göre toplamları grupla
    const getCurrencyTotals = () => {
        const activeCurrencies = activePayments.map(p => p.currency);
        // Currency enum'ı varsa onu kullan, yoksa veriden gelen unique currency'leri al
        const currenciesToCheck = Currency ? Object.keys(Currency).filter(k => activeCurrencies.includes(k)) : [...new Set(activeCurrencies)];

        return currenciesToCheck.map(currency => {
            const list = activePayments.filter(p => p.currency === currency);
            if (!list.length) return null;
            const total = list.map(x => x.amount).reduce((a, b) => a + b, 0);
            return { currency, total };
        }).filter(Boolean);
    };

    const currencyTotals = getCurrencyTotals();

    // Ödemelerden biri randevu para biriminden farklıysa orijinal tutar da gösterilir
    const hasForeignCurrency = activePayments.some(p => p.currency && p.currency !== appointmentCurrency);

    // --- MASAÜSTÜ TABLO KOLONLARI ---
    // Liste tek randevunun ödemeleri: randevu no sütunu yok. İkon/renk yok; renk yalnızca tutarda.
    const columns = [
        {
            key: 'paymentDate',
            dataIndex: 'paymentDate',
            title: t('Date'),
            width: 150,
            render: (text, row) => (
                <Space size={6}>
                    <span style={{ whiteSpace: 'nowrap' }}>{Helper.Date.dateFormat(text)}</span>
                    {row.isDeleted && <Tag style={{ margin: 0 }}>{t('Deleted')}</Tag>}
                </Space>
            )
        },
        {
            key: 'cashRegister',
            dataIndex: 'cashRegister',
            title: t('Cash Register'),
            width: 120,
            render: (text, row) => row.cashRegister?.name || '-'
        },
        {
            key: 'paymentMethod',
            dataIndex: 'paymentMethod',
            title: t('Method'),
            width: 120,
            render: (text, row) => row.paymentMethod?.name || '-'
        },
        {
            key: 'description',
            dataIndex: 'description',
            title: t('Description'),
            ellipsis: { showTitle: false },
            render: (text) => text
                ? <Tooltip title={text}><span>{text}</span></Tooltip>
                : <Text type="secondary">-</Text>
        },
        {
            key: 'amount',
            dataIndex: 'amount',
            title: t('Amount'),
            align: 'right',
            width: 140,
            render: (text, row) => (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Text strong delete={row.isDeleted} type={row.isDeleted ? 'secondary' : undefined} style={{ whiteSpace: 'nowrap' }}>
                        {formatPrice(row.currencyAmount, { currency: appointmentCurrency })}
                    </Text>
                    {row.currency && row.currency !== appointmentCurrency && (
                        <Text type="secondary" delete={row.isDeleted} style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                            {formatPrice(row.amount, { currency: row.currency })}
                        </Text>
                    )}
                </div>
            )
        },
    ];

    return (
        <div className="payments-container">
            {/* Responsive CSS */}
            <style>{`
                .desktop-table { display: block; }
                .mobile-list { display: none; }

                .payment-row-deleted > td { background-color: #fafafa !important; opacity: .55; }
                .payment-row-deleted:hover > td { background-color: #f0f0f0 !important; }
                
                @media (max-width: 768px) {
                    .desktop-table { display: none; }
                    .mobile-list { display: block; }
                }
            `}</style>

            {/* --- DESKTOP TABLE --- */}
            <div className="desktop-table">
                <Table
                    size={'small'}
                    dataSource={payments}
                    rowKey={'id'}
                    rowClassName={(row) => row?.isDeleted ? 'payment-row-deleted' : ''}
                    pagination={false}
                    columns={columns}
                    tableLayout="fixed"
                    locale={{ emptyText: <Empty description={t('No Payments')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    summary={() => activePayments.length > 0 && (
                        <Table.Summary>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={4} align="right">
                                    <Text type="secondary">{t('Total Payments')}</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                        <Text strong style={{ whiteSpace: 'nowrap' }}>
                                            {formatPrice(totalEquivalent, { currency: appointmentCurrency || 'TRY' })}
                                        </Text>
                                        {/* Para birimi dökümü yalnızca yabancı para ile ödeme varsa */}
                                        {hasForeignCurrency && currencyTotals.map((item, idx) => (
                                            <Text key={idx} type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                                                {formatPrice(item.total, { currency: item.currency || 'TRY' })}
                                            </Text>
                                        ))}
                                    </div>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />
            </div>

            {/* --- MOBİL LİSTE — ödeme modalındaki (MultiplePaymentApp) dar düzenle aynı:
                 tarih + kasa · yöntem · açıklama solda, tutar sağda; tek kutuda ince çizgili satırlar --- */}
            <div className="mobile-list">
                {payments.length === 0 ? (
                    <Empty description={t('No Payments')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, background: '#fafafa', overflow: 'hidden' }}>
                        {payments.map((row, index) => (
                            <div
                                key={row.id}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    gap: 8,
                                    padding: '8px 12px',
                                    borderTop: index === 0 ? 'none' : '1px solid #f0f0f0',
                                    opacity: row.isDeleted ? .55 : 1,
                                    fontSize: 12,
                                    color: '#595959',
                                }}
                            >
                                <div style={{ minWidth: 0 }}>
                                    <div>
                                        {Helper.Date.dateFormat(row.paymentDate)}
                                        {row.isDeleted && <Tag style={{ marginLeft: 6 }}>{t('Deleted')}</Tag>}
                                    </div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {[row.cashRegister?.name, row.paymentMethod?.name, row.description].filter(Boolean).join(' · ')}
                                    </Text>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 0 }}>
                                    <Text delete={row.isDeleted} type={row.isDeleted ? 'secondary' : undefined}
                                          style={{ whiteSpace: 'nowrap', fontWeight: 600, color: row.isDeleted ? undefined : (row.currencyAmount || 0) < 0 ? '#cf1322' : '#595959' }}>
                                        {formatPrice(row.currencyAmount, { currency: appointmentCurrency })}
                                    </Text>
                                    {row.currency && row.currency !== appointmentCurrency && (
                                        <Text type="secondary" delete={row.isDeleted} style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                                            {formatPrice(row.amount, { currency: row.currency })}
                                        </Text>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Toplam — masaüstü tablonun özet satırıyla aynı; döküm yalnız yabancı para varsa */}
                        {activePayments.length > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, padding: '8px 12px', borderTop: '1px solid #f0f0f0', background: '#fff', fontSize: 12 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>{t('Total Payments')}</Text>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    <Text strong style={{ whiteSpace: 'nowrap' }}>
                                        {formatPrice(totalEquivalent, { currency: appointmentCurrency || 'TRY' })}
                                    </Text>
                                    {hasForeignCurrency && currencyTotals.map((item, idx) => (
                                        <Text key={idx} type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                                            {formatPrice(item.total, { currency: item.currency || 'TRY' })}
                                        </Text>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}